import React, { useEffect, useRef, useState } from 'react';
import { CalendarCheck2, Loader2, RotateCcw, Send, Sparkles, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { fmtHora, mensajeError, textoDia, ymdDe } from '../../lib/calendario';

/* Aurora en el calendario (2026-10-06): «crea una reunión con Laura el jueves a las 5», «muévela a las 6».
   La conversación vive en el servidor (`/calendar/aurora`): es la misma en la web y en la app, y recuerda los eventos
   que creó aunque hayan pasado horas. Las acciones las ejecuta el servidor con los mismos permisos que el editor. */
const SUGERENCIAS = [
  '¿Qué tengo mañana?',
  'Crea una reunión de equipo el lunes a las 10 a. m. con videollamada',
  'Bloquea mi agenda el viernes por la tarde',
];

function Accion({ a, tz, onAbrir }) {
  const verbo = { crear: 'Creado', modificar: 'Cambiado', borrar: 'Borrado' }[a.tipo] || a.tipo;
  const cuando = a.inicio ? (a.todo_el_dia ? textoDia(ymdDe(a.inicio, tz), { weekday: 'short', day: 'numeric', month: 'short' })
    : `${textoDia(ymdDe(a.inicio, tz), { weekday: 'short', day: 'numeric', month: 'short' })} · ${fmtHora(a.inicio, tz)}`) : '';
  return (
    <div className={`cal-aurora-accion ${a.ok ? '' : 'is-error'}`}>
      {a.tipo === 'borrar' ? <Trash2 className="h-4 w-4 shrink-0" /> : <CalendarCheck2 className="h-4 w-4 shrink-0" />}
      <span className="min-w-0 flex-1">
        <span className="block truncate" style={{ fontWeight: 600 }}>{a.ok ? verbo : 'No se pudo'} · {a.titulo || '(Sin título)'}</span>
        {(cuando || a.error) && <span className="block truncate" style={{ color: 'var(--text-3)', fontSize: 12 }}>{a.ok ? cuando : a.error}</span>}
      </span>
      {a.ok && a.tipo !== 'borrar' && a.evento_id && (
        <button type="button" className="cal-mini-enlace" onClick={() => onAbrir(a.evento_id)}>Abrir</button>
      )}
    </div>
  );
}

export default function AuroraCalendario({ abierto, onCerrar, tz, onCambios, onAbrirEvento }) {
  const [mensajes, setMensajes] = useState(null);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [tarda, setTarda] = useState(false);   // más de 6 s: se dice que sigue pensando (no parezca colgado)
  const fin = useRef(null);
  const caja = useRef(null);

  useEffect(() => {
    if (!abierto || mensajes) return;
    api.get('/calendar/aurora').then(({ data }) => setMensajes(data.mensajes || [])).catch(() => setMensajes([]));
  }, [abierto, mensajes]);

  useEffect(() => { fin.current?.scrollIntoView({ block: 'end' }); }, [mensajes, enviando]);
  useEffect(() => { if (abierto) setTimeout(() => caja.current?.focus(), 50); }, [abierto]);

  const enviar = async (t = texto) => {
    const limpio = t.trim();
    if (!limpio || enviando) return;
    setTexto('');
    setEnviando(true);
    setTarda(false);
    const reloj = setTimeout(() => setTarda(true), 6000);
    setMensajes((l) => [...(l || []), { rol: 'yo', texto: limpio, en: new Date().toISOString(), acciones: [] }]);
    try {
      const { data } = await api.post('/calendar/aurora', { texto: limpio });
      setMensajes((l) => [...(l || []).slice(0, -1), data.pregunta, data.respuesta]);
      if ((data.respuesta.acciones || []).some((a) => a.ok)) onCambios?.();
    } catch (err) {
      setMensajes((l) => (l || []).slice(0, -1));
      setTexto(limpio);
      toast.error(mensajeError(err, 'Aurora no pudo responder'));
    } finally {
      clearTimeout(reloj);
      setEnviando(false);
      setTarda(false);
    }
  };

  const nueva = async () => {
    try { await api.delete('/calendar/aurora'); setMensajes([]); } catch { toast.error('No se pudo empezar de nuevo'); }
  };

  if (!abierto) return null;
  return (
    <aside className="cal-aurora" aria-label="Aurora en el calendario" data-testid="cal-aurora">
      <header className="cal-aurora-cab">
        <Sparkles className="h-4 w-4" style={{ color: 'var(--accent)' }} />
        <span className="flex-1" style={{ fontWeight: 600 }}>Aurora</span>
        {mensajes?.length > 0 && (
          <button type="button" className="cal-icono-btn" onClick={nueva} title="Nueva conversación" aria-label="Nueva conversación"><RotateCcw className="h-4 w-4" /></button>
        )}
        <button type="button" className="cal-icono-btn" onClick={onCerrar} aria-label="Cerrar"><X className="h-4 w-4" /></button>
      </header>
      <div className="cal-aurora-lista od-scroll">
        {mensajes === null ? (
          <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 od-spin" style={{ color: 'var(--accent)' }} /></div>
        ) : mensajes.length === 0 ? (
          <div className="cal-aurora-vacio">
            <p>Pídeme que cree, mueva o borre eventos, o pregúntame por tu agenda. Me acuerdo de lo que hagamos aquí.</p>
            {SUGERENCIAS.map((s) => <button key={s} type="button" className="cal-aurora-sug" onClick={() => enviar(s)}>{s}</button>)}
          </div>
        ) : mensajes.map((m, i) => (
          <div key={i} className={`cal-aurora-msj ${m.rol === 'yo' ? 'is-yo' : ''}`}>
            <div className="cal-aurora-burbuja">{m.texto}</div>
            {(m.acciones || []).map((a, j) => <Accion key={j} a={a} tz={tz} onAbrir={onAbrirEvento} />)}
          </div>
        ))}
        {enviando && (
          <div className="cal-aurora-msj">
            <div className="cal-aurora-burbuja flex items-center gap-2">
              <Loader2 className="h-4 w-4 od-spin" />{tarda && <span style={{ color: 'var(--text-3)' }}>Aurora está pensando…</span>}
            </div>
          </div>
        )}
        <div ref={fin} />
      </div>
      <form className="cal-aurora-pie" onSubmit={(e) => { e.preventDefault(); enviar(); }}>
        <textarea ref={caja} rows={1} className="cal-input cal-aurora-caja" value={texto} placeholder="Ej.: reunión con Laura el jueves a las 5 p. m."
          onChange={(e) => setTexto(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
          aria-label="Mensaje para Aurora" data-testid="cal-aurora-texto" />
        <button type="submit" className="od-btn-primary cal-aurora-enviar" disabled={!texto.trim() || enviando} aria-label="Enviar"><Send className="h-4 w-4" /></button>
      </form>
    </aside>
  );
}
